/* Append each search page as the visitor approaches the end of the results.
 * The next-page link remains usable without JavaScript and after a fetch error.
 */
(() => {
  if (customElements.get('lb-search-infinite')) return;

  class LbSearchInfinite extends HTMLElement {
    connectedCallback() {
      this.pager = this.closest('[data-lb-search-pager]');
      this.results = this.closest('[data-fragment-id="results"]');
      this.link = this.querySelector('[data-lb-search-next]');
      this.status = this.querySelector('[data-lb-search-status]');
      if (!this.pager || !this.results || !this.link) return;

      this.link.hidden = true;
      this.onLinkClick = (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.loadNext();
      };
      this.link.addEventListener('click', this.onLinkClick);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        }, { rootMargin: '0px 0px 500px 0px' });
        this.observer.observe(this.pager);
      } else {
        this.link.hidden = false;
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.link?.removeEventListener('click', this.onLinkClick);
    }

    async loadNext() {
      if (this.loading || !this.link || !this.isConnected) return;
      this.loading = true;
      this.observer?.disconnect();
      this.status.textContent = 'Loading more results…';
      this.controller = new AbortController();

      try {
        const url = new URL(this.link.href, window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Search page ${response.status}`);

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextResults = page.querySelector('[data-fragment-id="results"]');
        const nextContent = [...(nextResults?.children || [])]
          .filter((child) => !child.matches('[data-lb-search-pager]'));
        if (!nextContent.length) throw new Error('Empty search page');

        for (const child of nextContent) {
          this.results.insertBefore(document.adoptNode(child), this.pager);
        }

        const nextLink = page.querySelector('[data-lb-search-next]');
        if (nextLink) {
          const nextUrl = new URL(nextLink.href, window.location.href);
          nextUrl.searchParams.delete('section_id');
          this.link.href = nextUrl.toString();
          this.status.textContent = '';
          this.link.hidden = true;
          this.observer?.observe(this.pager);
        } else {
          this.pager.remove();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = '';
          this.link.hidden = false;
        }
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define('lb-search-infinite', LbSearchInfinite);
})();
